import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Car, 
  MapPin, 
  MessageCircle, 
  Shield, 
  Star, 
  Users, 
  Zap,
  CreditCard,
  Bell,
  FileText,
  TrendingUp
} from "lucide-react";

export const TimelyFeatures = () => {
  const customerFeatures = [
    {
      icon: <Car className="w-6 h-6" />,
      title: "Easy Booking",
      description: "Book your vehicle wash in just a few taps through our intuitive interface or WhatsApp",
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "bg-blue-500",
      textColor: "text-blue-700"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Find Nearby Centers",
      description: "Discover the best wash centers near your location with real-time availability",
      color: "bg-gradient-to-br from-green-50 to-green-100",
      iconColor: "bg-green-500",
      textColor: "text-green-700"
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "WhatsApp Integration",
      description: "Get booking confirmations, updates, and invoices directly on WhatsApp",
      color: "bg-gradient-to-br from-purple-50 to-purple-100",
      iconColor: "bg-purple-500",
      textColor: "text-purple-700"
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Secure Payments",
      description: "Pay safely with multiple payment options including UPI, cards, and wallets",
      color: "bg-gradient-to-br from-orange-50 to-orange-100",
      iconColor: "bg-orange-500",
      textColor: "text-orange-700"
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Real-time Updates",
      description: "Get notified about booking status, wash progress, and completion",
      color: "bg-gradient-to-br from-red-50 to-red-100",
      iconColor: "bg-red-500",
      textColor: "text-red-700"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Digital Invoices",
      description: "Receive detailed invoices and receipts directly on your phone",
      color: "bg-gradient-to-br from-indigo-50 to-indigo-100",
      iconColor: "bg-indigo-500",
      textColor: "text-indigo-700"
    }
  ];

  const vendorFeatures = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Customer Management",
      description: "Manage your customers, bookings, and service history in one place",
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "bg-blue-500",
      textColor: "text-blue-700"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Business Analytics",
      description: "Track your performance with detailed analytics and insights",
      color: "bg-gradient-to-br from-green-50 to-green-100",
      iconColor: "bg-green-500",
      textColor: "text-green-700"
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "WhatsApp Business",
      description: "Communicate with customers and manage bookings through WhatsApp",
      color: "bg-gradient-to-br from-purple-50 to-purple-100",
      iconColor: "bg-purple-500",
      textColor: "text-purple-700"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure Payments",
      description: "Receive payments securely with automated settlement and tracking",
      color: "bg-gradient-to-br from-orange-50 to-orange-100",
      iconColor: "bg-orange-500",
      textColor: "text-orange-700"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Reviews & Ratings",
      description: "Build your reputation with customer reviews and ratings",
      color: "bg-gradient-to-br from-yellow-50 to-yellow-100",
      iconColor: "bg-yellow-500",
      textColor: "text-yellow-700"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Quick Onboarding",
      description: "Get started in minutes with our simple registration process",
      color: "bg-gradient-to-br from-red-50 to-red-100",
      iconColor: "bg-red-500",
      textColor: "text-red-700"
    }
  ];

  return (
    <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <Badge variant="secondary" className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200 mb-4 sm:mb-6 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium inline-flex items-center max-w-full">
            <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
            <span className="break-words">Why Choose Timelyyy</span>
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 lg:mb-8 leading-tight px-2 break-words">
            Everything You Need for
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent"> Vehicle Wash</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-2 break-words">
            Whether you're a customer looking for convenient vehicle wash services or a wash center owner wanting to grow your business, we've got you covered with cutting-edge technology and seamless user experience.
          </p>
        </div>

        {/* Customer Features */}
        <div className="mb-12 sm:mb-16 lg:mb-20">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-6 px-2 break-words">For Customers</h3>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-2 break-words">Book, track, and pay for your vehicle wash with ease using our intuitive platform</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {customerFeatures.map((feature, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-500 border-0 bg-white/80 backdrop-blur-sm hover:bg-white/90 transform hover:-translate-y-2 min-w-0">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl ${feature.color} flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg flex-shrink-0`}>
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 ${feature.iconColor} rounded-lg sm:rounded-xl flex items-center justify-center`}>
                      <div className="text-white">
                        {feature.icon}
                      </div>
                    </div>
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors break-words">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <CardDescription className="text-gray-600 leading-relaxed text-sm sm:text-base break-words">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Vendor Features */}
        <div>
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-6 px-2 break-words">For Wash Centers</h3>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-2 break-words">Grow your business with our comprehensive management tools and analytics</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {vendorFeatures.map((feature, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-500 border-0 bg-white/80 backdrop-blur-sm hover:bg-white/90 transform hover:-translate-y-2 min-w-0">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl ${feature.color} flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg flex-shrink-0`}>
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 ${feature.iconColor} rounded-lg sm:rounded-xl flex items-center justify-center`}>
                      <div className="text-white">
                        {feature.icon}
                      </div>
                    </div>
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-gray-800 transition-colors break-words">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <CardDescription className="text-gray-600 leading-relaxed text-sm sm:text-base break-words">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
